import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { api } from '../api';
import styles from './Profile.module.css';

export default function Profile() {
  const { user, refreshUser, logout } = useAuth();
  const { success, error: toastError } = useToast();
  const navigate = useNavigate();

  const [profileForm, setProfileForm] = useState({
    displayName: user?.displayName || user?.display_name || '',
    email: user?.email || '',
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const setProfile = k => e => setProfileForm(f => ({ ...f, [k]: e.target.value }));
  const setPass    = k => e => setPasswordForm(f => ({ ...f, [k]: e.target.value }));

  const saveProfile = async e => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      await api.updateProfile({
        displayName: profileForm.displayName,
        email: profileForm.email !== user?.email ? profileForm.email : undefined,
      });
      await refreshUser();
      success('Profile updated successfully');
    } catch (err) {
      toastError(err.message || 'Failed to update profile');
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async e => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toastError('New passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toastError('Password must be at least 8 characters');
      return;
    }
    if (!/[A-Z]/.test(passwordForm.newPassword) || !/\d/.test(passwordForm.newPassword)) {
      toastError('Password must contain an uppercase letter and a number');
      return;
    }
    setSavingPassword(true);
    try {
      // Password change goes through reset-password flow in production
      // For now we update via profile endpoint — extend API as needed
      await api.updateProfile({ password: passwordForm.newPassword });
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      success('Password changed. Please log in again.');
      setTimeout(() => { logout(); navigate('/login'); }, 1500);
    } catch (err) {
      toastError(err.message || 'Failed to change password');
    } finally {
      setSavingPassword(false);
    }
  };

  if (!user) { navigate('/login'); return null; }

  const TIER_NAMES = ['', 'Bronze', 'Silver', 'Gold', 'Diamond'];
  const TIER_CLS   = ['', 'tier-1', 'tier-2', 'tier-3', 'tier-4'];

  return (
    <div className={styles.page}>
      <div className="container" style={{ maxWidth: 720 }}>

        {/* Header */}
        <div className={styles.header}>
          <div className={styles.avatar}>
            {(user.displayName || user.display_name || user.username || '?')[0].toUpperCase()}
          </div>
          <div>
            <h1 className={styles.username}>{user.displayName || user.display_name || user.username}</h1>
            <p className={styles.email}>{user.email}</p>
            {user.role === 'admin' && (
              <span className="tag tag-red" style={{ marginTop: 6, display: 'inline-flex' }}>
                Admin
              </span>
            )}
          </div>
        </div>

        {/* Wallet summary */}
        <div className={styles.walletBanner}>
          <div className={styles.walletInfo}>
            <span className={styles.walletLabel}>Wallet balance</span>
            <span className={styles.walletAmount}>
              ${((user.walletBalance || 0) / 100).toFixed(2)}
            </span>
            <span className={styles.walletVotes}>
              = {Math.floor((user.walletBalance || 0) / 100)} votes
            </span>
          </div>
          <a href="/wallet" className="btn btn-magenta btn-sm">Add Funds</a>
        </div>

        {/* Profile form */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Profile Information</h2>
          <form onSubmit={saveProfile} className={styles.form}>
            <div className="form-group">
              <label className="form-label">Display name</label>
              <input
                className="form-input"
                value={profileForm.displayName}
                onChange={setProfile('displayName')}
                placeholder="How you appear on VoxPop"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email address</label>
              <input
                className="form-input"
                type="email"
                value={profileForm.email}
                onChange={setProfile('email')}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Username</label>
              <input
                className="form-input"
                value={user.username}
                disabled
                style={{ opacity: 0.5, cursor: 'not-allowed' }}
              />
              <span style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>
                Username cannot be changed
              </span>
            </div>
            <button
              type="submit"
              className="btn btn-magenta"
              disabled={savingProfile}
              style={{ alignSelf: 'flex-start' }}
            >
              {savingProfile ? 'Saving…' : 'Save Profile'}
            </button>
          </form>
        </div>

        {/* Password form */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Change Password</h2>
          <form onSubmit={savePassword} className={styles.form}>
            <div className="form-group">
              <label className="form-label">New password</label>
              <input
                className="form-input"
                type="password"
                value={passwordForm.newPassword}
                onChange={setPass('newPassword')}
                required
                autoComplete="new-password"
              />
              <span style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4 }}>
                Min 8 characters, 1 uppercase, 1 number
              </span>
            </div>
            <div className="form-group">
              <label className="form-label">Confirm new password</label>
              <input
                className="form-input"
                type="password"
                value={passwordForm.confirmPassword}
                onChange={setPass('confirmPassword')}
                required
                autoComplete="new-password"
              />
            </div>
            <button
              type="submit"
              className="btn btn-outline-magenta"
              disabled={savingPassword}
              style={{ alignSelf: 'flex-start' }}
            >
              {savingPassword ? 'Changing…' : 'Change Password'}
            </button>
          </form>
        </div>

        {/* Danger zone */}
        <div className={`${styles.section} ${styles.dangerZone}`}>
          <h2 className={styles.sectionTitle} style={{ color: 'var(--coral)' }}>
            Danger Zone
          </h2>
          <p style={{ color: 'var(--gray)', fontSize: 14, marginBottom: 16 }}>
            Logging out will clear your session. Deleting your account is permanent and cannot be undone.
          </p>
          <div className={styles.dangerBtns}>
            <button
              className="btn btn-outline-magenta"
              onClick={() => { logout(); navigate('/'); }}
            >
              Log Out
            </button>
            {!showDeleteConfirm ? (
              <button
                className="btn"
                style={{ color: 'var(--coral)', border: '1.5px solid var(--coral)' }}
                onClick={() => setShowDeleteConfirm(true)}
              >
                Delete Account
              </button>
            ) : (
              <div className={styles.deleteConfirm}>
                <p style={{ fontSize: 14, color: 'var(--coral)', fontWeight: 600 }}>
                  Are you sure? This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
                  <button
                    className="btn btn-sm"
                    style={{ background: 'var(--coral)', color: '#fff' }}
                    onClick={() => {
                      // In production: call DELETE /api/auth/me, then logout
                      toastError('Account deletion coming soon — contact support for now.');
                      setShowDeleteConfirm(false);
                    }}
                  >
                    Yes, delete
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
